<template>
  <div class="clerk-top-bar">
    <div class="clerk-top-bar__brand">
      <ClerkLogo />
      <span class="clerk-top-bar__tag">{{ label }}</span>
    </div>

    <span class="clerk-top-bar__divider" />

    <div class="clerk-top-bar__identity">
      <span class="clerk-top-bar__sale">{{ saleName }}</span>
      <span class="clerk-top-bar__separator">·</span>
      <span class="clerk-top-bar__lane">{{ lane }}</span>
      <span class="clerk-top-bar__external-id">{{ externalId }}</span>
    </div>

    <span class="clerk-top-bar__spacer" />

    <div class="clerk-top-bar__right">
      <ConnectionPill :state="streamHealth" />
      <slot name="actions" />
      <!-- Who is signed in, and their settings. An application with an account
           menu of its own puts it here instead, and the bar's own cog and
           avatar give way to it. -->
      <slot name="account">
        <Button
          severity="secondary"
          icon="pi pi-cog"
          title="Settings"
          :style="iconButtonFootprint"
          @click="toggleSettings"
        />
        <Avatar
          :label="userInitials"
          shape="circle"
          :style="{
            width: '28px',
            height: '28px',
            background: userBackground || 'var(--p-surface-500)',
            color: 'var(--p-surface-0)',
            fontSize: '11px',
            fontWeight: '600',
            letterSpacing: '0.02em',
          }"
        />

        <Popover ref="settingsPopover">
          <div class="clerk-top-bar__settings">
            <span class="clerk-top-bar__settings-label">Appearance</span>
            <SelectButton
              v-model="appearance"
              :options="APPEARANCE_OPTIONS"
              option-label="label"
              option-value="value"
              :allow-empty="false"
              aria-label="Appearance"
            >
              <template #option="{ option }">
                <i :class="option.icon" class="clerk-top-bar__settings-icon" />
                <span>{{ option.label }}</span>
              </template>
            </SelectButton>
          </div>
        </Popover>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import Popover from 'primevue/popover';
import SelectButton from 'primevue/selectbutton';
import ClerkLogo from '../ClerkLogo/ClerkLogo.vue';
import ConnectionPill from '../ConnectionPill/ConnectionPill.vue';

// The console's top bar: wordmark, which console this is, which sale it is
// driving, stream health, settings, and who is signed in.
//
// It reached the appearance setting through `inject('setClerkTheme')` — a
// provide the application happened to make, which is a global by another name
// and left the component unusable anywhere that did not make it. The setting is
// a `v-model` now, so the application still owns light/dark and the top bar
// just reports the choice.
//
// It also took the whole `data` record and pulled `data.event` apart inside the
// template. The four fields it actually shows are four props.
//
// SLOTS
//   actions — extra controls before the settings cog.
defineOptions({ name: 'ClerkTopBar' });

defineProps({
  // Which console this is — "Legacy Clerk".
  label: { type: String, default: 'Clerk' },
  saleName: { type: String, default: '' },
  lane: { type: String, default: '' },
  // The sale's id in whatever system the auction house runs.
  externalId: { type: String, default: '' },
  // healthy | degraded | offline
  streamHealth: { type: String, default: 'healthy' },
  userInitials: { type: String, default: '' },
  // Per-person avatar tint, decided by the application.
  userBackground: { type: String, default: undefined },
});

// light | dark. Unbound, the toggle still works locally — which is what makes
// this demonstrable outside an application that owns a theme.
const appearance = defineModel({ type: String, default: 'dark' });

const APPEARANCE_OPTIONS = [
  { label: 'Light', value: 'light', icon: 'pi pi-sun' },
  { label: 'Dark', value: 'dark', icon: 'pi pi-moon' },
];

const settingsPopover = ref(null);
const toggleSettings = (event) => settingsPopover.value?.toggle(event);

// The square footprint the settings cog lays out against — structure, not
// theme, so it stays an inline style rather than a token.
const iconButtonFootprint = { width: '34px', height: '34px' };
</script>

<style scoped>
.clerk-top-bar {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  background: var(--p-clerk-top-bar-background);
  border-bottom: var(--p-clerk-top-bar-border-bottom-width) solid var(--p-clerk-top-bar-border-bottom-color);
  padding: var(--p-clerk-top-bar-padding);
  gap: var(--p-clerk-top-bar-gap);
}

.clerk-top-bar__brand {
  display: flex;
  align-items: center;
  gap: var(--p-clerk-top-bar-brand-gap);
}

.clerk-top-bar__tag {
  text-transform: uppercase;
  padding: var(--p-clerk-top-bar-tag-padding);
  border-radius: var(--p-clerk-top-bar-tag-border-radius);
  font-size: var(--p-clerk-top-bar-tag-font-size);
  font-weight: var(--p-clerk-top-bar-tag-font-weight);
  letter-spacing: var(--p-clerk-top-bar-tag-letter-spacing);
  background: var(--p-clerk-top-bar-tag-background);
  color: var(--p-clerk-top-bar-tag-color);
}

.clerk-top-bar__divider {
  width: 1px;
  height: 22px;
  background: var(--p-clerk-top-bar-divider-background);
}

.clerk-top-bar__identity {
  display: flex;
  align-items: baseline;
  gap: var(--p-clerk-top-bar-identity-gap);
}

.clerk-top-bar__sale {
  font-size: var(--p-clerk-top-bar-sale-font-size);
  font-weight: var(--p-clerk-top-bar-sale-font-weight);
  color: var(--p-clerk-top-bar-sale-color);
}

.clerk-top-bar__separator {
  color: var(--p-clerk-top-bar-separator-color);
}

.clerk-top-bar__lane {
  font-size: var(--p-clerk-top-bar-lane-font-size);
  font-weight: var(--p-clerk-top-bar-lane-font-weight);
  color: var(--p-clerk-top-bar-lane-color);
}

.clerk-top-bar__external-id {
  margin-left: 4px;
  font-family: var(--p-clerk-top-bar-external-id-font-family);
  font-size: var(--p-clerk-top-bar-external-id-font-size);
  color: var(--p-clerk-top-bar-external-id-color);
}

.clerk-top-bar__spacer {
  flex: 1;
}

.clerk-top-bar__right {
  display: flex;
  align-items: center;
  gap: var(--p-clerk-top-bar-right-gap);
}

.clerk-top-bar__settings {
  display: flex;
  flex-direction: column;
  min-width: 180px;
  gap: var(--p-clerk-top-bar-settings-gap);
}

.clerk-top-bar__settings-label {
  text-transform: uppercase;
  font-size: var(--p-clerk-top-bar-settings-label-font-size);
  font-weight: var(--p-clerk-top-bar-settings-label-font-weight);
  letter-spacing: var(--p-clerk-top-bar-settings-label-letter-spacing);
  color: var(--p-clerk-top-bar-settings-label-color);
}

.clerk-top-bar__settings-icon {
  margin-right: 6px;
  font-size: var(--p-clerk-top-bar-settings-icon-font-size);
}
</style>
